import Vue from 'vue';
import Vuex, { Store } from 'vuex';
import { QuoteServiceApi } from '@/resources/phrospr-api/quote.service';
import {
    IClientEntity,
    IConfiguration,
    IPlanningItemEntity,
    IPlanningItemReportEntity,
    IProductEntity,
    IQuoteEntity,
    IUserEntity,
    IWorkOrderEntity,
    PlanningItemApi,
    QuoteStatus,
    WorkOrderApi,
} from '@phrospr/api-backend';
import { initializeQuoteStore } from '@/store/quote/quote.store';
import getRawQuotes = QuoteServiceApi.getRawQuotes;
import { IQuoteState } from '@/store/quote/quote.state';
import { IWorkOrderState } from '@/store/work-order/workOrder.state';
import { initializeWorkOrderStore } from '@/store/work-order/workOrder.store';
import { ILocationsEntity } from '@phrospr/lib-models';
import { initializeInventoryStore } from '@/store/inventory/inventory.store';

Vue.use(Vuex);

export interface IStoreState {
    user: IUserEntity;
    configuration: Partial<IConfiguration>;
    planningItem: IPlanningItemEntity;

    editVisitReport: IPlanningItemReportEntity;
    quotes: IQuoteEntity[];
    quoteCreateSettings: IStateQuoteCreateSettings;

    quote: IQuoteState;
    clientSearch: IClientEntity[];
    productSearch: IProductEntity[];
    locationSearch: ILocationsEntity[];

    workOrder: IWorkOrderState;
    workOrders: IWorkOrderEntity[];
    workOrderCreateSettings: IStateWorkOrderCreateSettings;

    inventoryCreateSettings: IStateInventoryCreateSettings;
}

export interface IStateQuoteCreateSettings {
    client?: IClientEntity;
    createAsLead?: boolean;
    planningItem?: IPlanningItemEntity;
    editMode?: boolean;
    quote_id?: number;
    client_id?: number;
}
export interface IStateWorkOrderCreateSettings {
    client?: IClientEntity;
    planningItem?: IPlanningItemEntity;
    editMode?: boolean;
    workOrderId?: number;
    client_id?: number;
}

export interface IStateInventoryCreateSettings {
    product?: IProductEntity;
    location?: ILocationsEntity;
    editMode?: boolean;
    inventoryId?: number;
    productId?: number;
    locationId?: number;
}

export const appStore: Store<IStoreState> = new Vuex.Store<IStoreState>({
    modules: {
        quote: initializeQuoteStore(),
        workOrder: initializeWorkOrderStore(),
        inventory: initializeInventoryStore(),
    },
    state: {
        user: null,
        configuration: {},
        planningItem: null,
        editVisitReport: null,
        quotes: [],
        quoteCreateSettings: null,
        quote: null,
        clientSearch: [],
        productSearch: [],
        workOrder: null,
        workOrders: [],
        workOrderCreateSettings: null,
        locationSearch: [],
        inventoryCreateSettings: null,
    },
    mutations: {
        setUser(state, user) {
            state.user = user;
        },
        setConfiguration(state, configuration) {
            state.configuration = configuration;
        },
        setPlanningItem(state, planningItem) {
            state.planningItem = planningItem;
        },
        resetPlanningItem(state) {
            state.planningItem = null;
            state.editVisitReport = null;
        },
        editPlanningItemReport(state, planningItemReport: IPlanningItemReportEntity) {
            state.editVisitReport = planningItemReport;
        },
        setQuotes(state, quotes) {
            state.quotes = quotes;
        },
        resetQuotes(state) {
            state.quotes = null;
        },
        setQuoteCreateSettings(state, quoteCreateSettings: IStateQuoteCreateSettings) {
            state.quoteCreateSettings = quoteCreateSettings;
        },
        setWorkOrderCreateSettings(state, workOrderCreateSettings: IStateWorkOrderCreateSettings) {
            state.workOrderCreateSettings = workOrderCreateSettings;
        },
        setWorkOrders(state, workOrders) {
            state.workOrders = workOrders;
        },
        resetWorkOrders(state) {
            state.workOrders = null;
        },
        setClientSearch(state, clients: IClientEntity[]) {
            state.clientSearch = clients;
        },
        setProductSearch(state, products: IProductEntity[]) {
            state.productSearch = products;
        },
        setLocationSearch(state, locations: ILocationsEntity[]) {
            state.locationSearch = locations;
        },
        setInventoryCreateSettings(state, inventoryCreateSettings: IStateInventoryCreateSettings) {
            state.inventoryCreateSettings = inventoryCreateSettings;
        },
    },
    actions: {
        async getPlanningDetailFromCalendarItemId(context, calendarItemId: number) {
            context.commit('resetPlanningItem');
            try {
                const planningItem = (await PlanningItemApi.getPlanningItemDetail(calendarItemId)).data;
                context.commit('setPlanningItem', planningItem);
            } catch (err) {
                console.log('failed dispatching getPlanningDetailFromCalendarItemId');
                console.error(err);
            }
        },
        async refreshPlanningItem(context) {
            const currentPlanningItem = context.state.planningItem;
            if (!currentPlanningItem) {
                console.error('currently no planning item found');
                return;
            }
            // Refresh in place: nulling the planning item first unmounts the detail
            // md-tabs mid-update, which crashes vue-material's tab resize observer.
            const planningItem = (
                await PlanningItemApi.getPlanningItemDetail(currentPlanningItem.selected_calendar_item.calendar_item_id)
            ).data;

            context.commit('setPlanningItem', planningItem); // try catch needed?
        },
        async getWorkOrdersFromPlanningItem(context, planning_item_id: number) {
            context.commit('resetWorkOrders');
            try {
                const workOrders = (await WorkOrderApi.getWorkOrdersFromPlanningItem(planning_item_id)).data;
                context.commit('setWorkOrders', workOrders);
            } catch (err) {
                console.log('failed dispatching getWorkOrdersFromPlanningItem');
                console.error(err);
            }
        },
        async getQuotesFromPlanningItemForLeadId(context, planning_item_id: number) {
            context.commit('resetQuotes');
            try {
                const quotes = (
                    await getRawQuotes(
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        planning_item_id,
                        undefined,
                        true,
                    )
                ).records;
                context.commit('setQuotes', quotes);
            } catch (err) {
                console.log('failed dispatching getQuotesFromPlanningItemForLeadId');
                console.error(err);
            }
        },
        async getQuotesFromPlanningItemID(context, planningItemId: number) {
            context.commit('resetQuotes');
            try {
                const quotes = (
                    await getRawQuotes(
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        [QuoteStatus.approved],
                        planningItemId,
                        undefined,
                        true,
                    )
                ).records;
                context.commit('setQuotes', quotes);
            } catch (err) {
                console.log('failed dispatching getQuotesFromPlanningItemID');
                console.error(err);
            }
        },
    },
});
